import { Link } from "react-router-dom";
import { ArrowRight, Phone, Mail, MapPin, Sparkles, Flame, Utensils, GlassWater, ChefHat, CheckCircle2 } from "lucide-react";
import Logo from "@/components/Logo";

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#050505] text-white relative overflow-x-hidden" data-testid="landing-page">
      {/* Hero */}
      <section className="relative min-h-[100svh] flex flex-col">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1763645246808-79c2c51d86ad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjByZXN0YXVyYW50JTIwZGFyayUyMGFtYmlhbmNlfGVufDB8fHx8MTc3NzI1NTc1N3ww&ixlib=rb-4.1.0&q=85')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/40 via-[#050505]/70 to-[#050505]" />

        <header className="relative z-10 px-5 sm:px-10 py-6 flex items-center justify-between">
          <Logo size="sm" />
          <nav className="flex items-center gap-3 sm:gap-5">
            <Link to="/menu" className="text-xs sm:text-sm uppercase tracking-[0.2em] text-white/70 hover:text-[#C9963F] transition-colors" data-testid="nav-public-menu">Menu</Link>
            <Link to="/inquire" className="hidden sm:inline-flex items-center gap-2 bg-[#C9963F] text-black px-4 py-2 text-sm font-medium hover:bg-[#A67A2D] transition-colors" data-testid="nav-inquire">
              Submit Inquiry <ArrowRight size={14} />
            </Link>
            <span className="hidden sm:inline-block w-px h-5 bg-white/15" />
            <Link to="/employee/login" className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-white/50 hover:text-[#C9963F] transition-colors" data-testid="nav-employee-login">Staff</Link>
            <Link to="/admin/login" className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-white/50 hover:text-[#C9963F] transition-colors" data-testid="nav-admin-login">Admin</Link>
          </nav>
        </header>

        <div className="relative z-10 flex-1 flex items-center px-5 sm:px-10 lg:px-20 pb-12">
          <div className="max-w-3xl fade-up">
            <div className="text-[10px] sm:text-xs uppercase tracking-[0.4em] text-[#C9963F] mb-5">
              Premium Catering · The Colony, TX
            </div>
            <h1 className="font-serif-brand text-5xl sm:text-6xl lg:text-7xl leading-[1.05] mb-6">
              <span className="italic">A taste of</span><br />
              <span className="text-[#C9963F]">Sheesh,</span> at your event.
            </h1>
            <p className="text-base sm:text-lg text-white/70 max-w-2xl leading-relaxed">
              Live BBQ stations, hookah service, mocktail bars, and signature buffets — crafted by Sheesh Eatery & Lounge for weddings, corporate events, and intimate gatherings across DFW.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/inquire" data-testid="hero-inquire-btn"
                className="inline-flex items-center gap-2 bg-[#C9963F] text-black px-6 sm:px-8 py-3 sm:py-4 text-sm font-medium hover:bg-[#A67A2D] transition-colors">
                Submit Inquiry <ArrowRight size={16} />
              </Link>
              <Link to="/menu" data-testid="hero-menu-btn"
                className="inline-flex items-center gap-2 border border-white/20 text-white px-6 sm:px-8 py-3 sm:py-4 text-sm hover:border-[#C9963F] hover:text-[#C9963F] transition-colors">
                View Menu
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="px-5 sm:px-10 lg:px-20 py-20 sm:py-28">
        <div className="max-w-6xl mx-auto">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#C9963F] mb-3">What we do</div>
          <h2 className="font-serif-brand text-4xl sm:text-5xl mb-12 max-w-2xl">
            Three signature services. <span className="text-white/40">One unforgettable night.</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Utensils, title: "Food Catering", desc: "Live BBQ, curry stations, biryani, chaat — full buffet or plated service for any size event." },
              { icon: GlassWater, title: "Mocktail Bar", desc: "Custom mocktail menus crafted on-site by our bartenders. Refreshing, photo-worthy, alcohol-free." },
              { icon: Flame, title: "Hookah Service", desc: "Premium flavors with attendants for outdoor & well-ventilated venues. Sets the mood instantly." },
            ].map((s, i) => (
              <div key={s.title} className={`bg-[#0e0e0e] border border-white/10 p-7 hover:border-[#C9963F]/40 transition-colors fade-up delay-${i + 1}`}>
                <s.icon size={28} strokeWidth={1.5} className="text-[#C9963F] mb-5" />
                <h3 className="font-serif-brand text-2xl mb-3">{s.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="px-5 sm:px-10 lg:px-20 py-20 sm:py-28 bg-[#0a0a0a] border-y border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#C9963F] mb-3">How it works</div>
          <h2 className="font-serif-brand text-4xl sm:text-5xl mb-12">From inquiry to event day.</h2>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { n: "01", title: "Tell us about your event", desc: "Share the date, time, and what you're dreaming of — through the inquiry form." },
              { n: "02", title: "Pick your menu", desc: "Browse our full menu and tag the items you'd like to feature." },
              { n: "03", title: "We send a quote", desc: "Within 24 hours, you'll get a tailored estimate. No surprises." },
              { n: "04", title: "Sign & celebrate", desc: "Approve, secure with a 50% deposit, and we handle the rest." },
            ].map((p) => (
              <div key={p.n} className="border-l-2 border-[#C9963F]/40 pl-5">
                <div className="text-[#C9963F] font-serif-brand text-3xl mb-2">{p.n}</div>
                <div className="text-white text-base mb-2">{p.title}</div>
                <div className="text-white/50 text-sm leading-relaxed">{p.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Sheesh */}
      <section className="px-5 sm:px-10 lg:px-20 py-20 sm:py-28">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#C9963F] mb-3">Why Sheesh</div>
            <h2 className="font-serif-brand text-4xl sm:text-5xl mb-6">Restaurant-grade. Event-ready.</h2>
            <p className="text-white/60 leading-relaxed mb-8">
              Sheesh Eatery & Lounge is a full-service restaurant in The Colony — which means our caterings are powered by a real kitchen, real chefs, and real standards. No reheated trays, no shortcuts.
            </p>
            <ul className="space-y-3">
              {[
                "Fresh-prepared the day of your event",
                "ChefHat-led setup 30–60 min before service",
                "Hookah, mocktail, and live stations all in-house",
                "Transparent quotes with no hidden fees",
              ].map((p) => (
                <li key={p} className="flex items-start gap-3 text-white/80 text-sm">
                  <CheckCircle2 size={16} className="text-[#C9963F] mt-0.5 shrink-0" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
          <div
            className="aspect-[4/5] bg-cover bg-center grain"
            style={{
              backgroundImage:
                "url('https://images.pexels.com/photos/24863057/pexels-photo-24863057.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
            }}
            aria-hidden
          />
        </div>
      </section>

      {/* CTA */}
      <section className="px-5 sm:px-10 lg:px-20 py-20 sm:py-28 bg-[#0a0a0a] border-t border-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <Sparkles size={24} className="text-[#C9963F] mx-auto mb-5" strokeWidth={1.5} />
          <h2 className="font-serif-brand text-4xl sm:text-5xl mb-5">Let's build your menu.</h2>
          <p className="text-white/60 mb-10 max-w-xl mx-auto">
            Tell us a little about your event and pick the items you'd like. We'll reply with a personalized quote within a day.
          </p>
          <Link to="/inquire" data-testid="cta-inquire-btn"
            className="inline-flex items-center gap-2 bg-[#C9963F] text-black px-8 py-4 text-sm font-medium hover:bg-[#A67A2D] transition-colors">
            Submit Inquiry <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-5 sm:px-10 lg:px-20 py-12 border-t border-white/10">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 text-sm">
          <div>
            <Logo size="sm" />
            <p className="text-white/40 mt-3 text-xs">A premium catering & event experience by Sheesh Eatery & Lounge.</p>
          </div>
          <div className="space-y-2 text-white/60">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9963F] mb-2">Visit</div>
            <div className="flex items-start gap-2"><MapPin size={14} className="mt-0.5 shrink-0" /> 4707 State Hwy 121, The Colony, TX 75056</div>
            <div className="flex items-start gap-2"><Phone size={14} className="mt-0.5 shrink-0" /> (214) 310-7650</div>
            <div className="flex items-start gap-2"><Mail size={14} className="mt-0.5 shrink-0" /> sheeshtx@gmail.com</div>
          </div>
          <div className="text-white/60">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9963F] mb-2">Catering</div>
            <Link to="/menu" className="block hover:text-[#C9963F] mb-1.5">Browse menu</Link>
            <Link to="/inquire" className="block hover:text-[#C9963F] mb-1.5">Submit inquiry</Link>
            <Link to="/admin/login" className="block hover:text-[#C9963F] text-white/30 text-xs mt-4">Admin login</Link>
            <Link to="/employee/login" className="block hover:text-[#C9963F] text-white/30 text-xs">Staff login</Link>
          </div>
        </div>
        <div className="text-center text-[10px] uppercase tracking-[0.3em] text-white/30 mt-10">
          © {new Date().getFullYear()} Sheesh Eatery & Lounge
        </div>
      </footer>
    </div>
  );
}
